import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Mobile filter drawer (SPEC-04 §5.2): the sidebar filters are SSR'd into
 * #catalog-filters (hidden under 900px); this widget renders a trigger
 * button and, on open, moves that markup into a bottom sheet. Links stay
 * plain GET navigations, so the drawer needs no state of its own.
 */
export default function FilterDrawer({ count = 0, label = "Filters" }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const movedRef = useRef(null);

  const moveFilters = useCallback((into) => {
    const source = document.getElementById("catalog-filters");
    if (!source) return;
    if (into && !movedRef.current) {
      movedRef.current = source.querySelector(".filters");
      if (movedRef.current && panelRef.current) {
        panelRef.current.appendChild(movedRef.current);
      }
    } else if (!into && movedRef.current) {
      source.appendChild(movedRef.current);
      movedRef.current = null;
    }
  }, []);

  const openDrawer = useCallback(() => {
    setOpen(true);
    // Defer until the panel is mounted.
    requestAnimationFrame(() => moveFilters(true));
  }, [moveFilters]);

  const closeDrawer = useCallback(() => {
    moveFilters(false);
    setOpen(false);
  }, [moveFilters]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, closeDrawer]);

  // A filter link tap navigates anyway; close immediately for feedback.
  useEffect(() => {
    if (!open || !panelRef.current) return undefined;
    const panel = panelRef.current;
    const onClick = (event) => {
      const link = event.target.closest("a");
      if (link) closeDrawer();
    };
    panel.addEventListener("click", onClick);
    return () => panel.removeEventListener("click", onClick);
  }, [open, closeDrawer]);

  return (
    <>
      <button type="button" className="filter-drawer__open" onClick={openDrawer}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        {label}
        {count > 0 && <span className="filter-drawer__count">{count}</span>}
      </button>
      {open && (
        <>
          <div
            className="filter-drawer__backdrop filter-drawer__backdrop--open"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <div
            className="filter-drawer__panel filter-drawer__panel--open"
            role="dialog"
            aria-modal="true"
            aria-label={label}
          >
            <div className="filter-drawer__grab" aria-hidden="true" />
            <div className="filter-drawer__head">
              <h2>{label}</h2>
              <button type="button" className="filter-drawer__close" onClick={closeDrawer} aria-label="Close">
                ×
              </button>
            </div>
            <div ref={panelRef} />
            <button type="button" className="filter-drawer__done" onClick={closeDrawer}>
              Done
            </button>
          </div>
        </>
      )}
    </>
  );
}
